import type { WellIndexType } from '@go-widgets/well-logs-widget';
import {
  FullArrowLight as FullIcon,
  FilterAltLight as FilterIcon,
  WidgetAddLight as WidgetAddIcon,
  SubttasksLight,
  VerticalCustom,
} from '@repo/go-icons';
import { Button, Loader, Tooltip } from '@repo/go-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { EXPORT_CSV_AVAILABLE, WELL_LOGS_MANAGER_AVAILABLE } from 'src/api/consts';
import { useService } from 'src/packages/di';
import { NewContentWidgetModal } from 'src/pages/dashboard/features/new-content-widget-modal/NewContentWidgetModal';
import { UserExportTasksButton } from 'src/pages/dashboard/features/user-export-tasks/UserExportTasksButton';
import { WellListPanelAsPopup } from 'src/pages/dashboard/features/well-list-panel-as-popup/WellListPanelAsPopup';
import { Workbench } from 'src/pages/dashboard/features/workbench/Workbench';

import { MasterDisconnectedModal } from '../features/master-disconnected-modal/MasterDisconnectedModal';

import { TabName } from './components/tab-button/TabName';
import styles from './SingleTab.module.scss';
import { SingleTabStore } from './SingleTab.store';

export const SingleTab = observer(function SingleTab() {
  const { id: tabId } = useParams();

  const [store, setStore] = useState<SingleTabStore | null>(null);

  const { t } = useTranslation();

  const destroyTab = store?.windowsManager?.destroyTab;

  const workspaceRef = useRef<HTMLDivElement>(null);
  const { isFullscreenMode, enterFullscreen, exitFullscreen } = useService('fullscreenService');

  const handleEnterFullscreen = () => {
    if (!workspaceRef.current) {
      return;
    }
    enterFullscreen(workspaceRef.current);
  };

  const onCreateWidget = (type: string, wellIndexType?: WellIndexType) => {
    store?.newContentWidgetController?.resolve({ type, wellIndexType });
  };

  const onCloseNewContentWidgetModal = () => {
    store?.newContentWidgetController?.resolve(null);
  };

  useEffect(() => {
    if (tabId && !store) {
      setStore(new SingleTabStore(tabId));
    }
  }, [tabId, store]);

  const effect = store?.effect ?? (() => {});

  useEffect(effect, [effect]);

  const directories = useService('directories');

  if (store?.hasError) {
    return (
      <main className={styles.wrapper}>
        <p>Error. Please reload the page</p>
      </main>
    );
  }

  if (!store || store.isLoading || !store.actualTab) {
    return <Loader size="l" className={styles.loader} />;
  }

  const {
    actualTab,
    isUpdating,
    onCreateNewContentWidget,
    newContentWidgetController,
    masterConnectionController,
    recoverMaster,
    ingoreMasterRecovering,
  } = store;

  const actualTabGroup = actualTab?.groupManager.groups.find(
    (group) => group.id === actualTab.wellListCombinedData.panelState?.groupId,
  );
  const wells = directories.getObject('GOdashboard_Well');
  const wellName =
    wells?.find((item) => item.id === actualTab?.wellListCombinedData.store.selectedWellId)?.data.name ??
    actualTab?.wellListCombinedData.store.selectedWellId;

  return (
    <main className={clsx(styles.wrapper, actualTab?.fullscreenWidget && styles.wrapperFullscreenMode)}>
      {isUpdating && <Loader className={styles.loader} />}

      <section className={styles.header}>
        <div className={styles.widgetsToolbar}>
          <Tooltip.Multi
            tooltips={[
              {
                key: 'wellListTooltip',
                title: wellName ?? t('dashboard:Toolbar.openWellsList'),
                delay: 400,
                open: store.isWellListInFSModeOpen ? false : undefined,
              },
              {
                key: 'wellListWidgetAsPopup',
                title: (
                  <WellListPanelAsPopup
                    actualTab={actualTab}
                    onClosePopup={() => store.setIsWellListInFSModeOpen(false)}
                  />
                ),
                open: store.isWellListInFSModeOpen,
                onOpenChange: (value) => store.setIsWellListInFSModeOpen(value),
                withArrow: false,
                trigger: ['click'],
                gap: 8,
                className: styles.wellListPopup,
              },
            ]}
          >
            <div style={{ position: 'relative' }}>
              <Button
                icon={<VerticalCustom className={styles.icon} />}
                className={clsx(styles.toolbarIcon)}
                style={{
                  backgroundColor: actualTabGroup?.color,
                  color: actualTabGroup?.contrastColor,
                }}
              />
            </div>
          </Tooltip.Multi>
          {WELL_LOGS_MANAGER_AVAILABLE && (
            <Tooltip title={t('widgets:WellLogsManager.title')}>
              <Button
                variant="flat"
                icon={<SubttasksLight className={styles.icon} />}
                onClick={() => actualTab?.createWellLogsManager?.()}
                className={clsx(styles.toolbarIcon)}
              />
            </Tooltip>
          )}
          <Tooltip title={t('dashboard:Toolbar.openFilters')} delay={400}>
            <Button
              variant="flat"
              icon={<FilterIcon className={styles.icon} />}
              onClick={() => actualTab?.createWellsFilterWidget?.()}
              className={styles.toolbarIcon}
            />
          </Tooltip>
          <Button
            variant="flat"
            icon={<WidgetAddIcon className={styles.icon} />}
            onClick={onCreateNewContentWidget}
            className={styles.toolbarIcon}
          />
        </div>
        <TabName onRemove={() => (tabId ? destroyTab?.(tabId) : void 0)}>{store.actualTab?.name}</TabName>

        <div className={clsx(styles.widgetsToolbar, styles.divider)} style={{ marginLeft: 'auto' }}>
          {EXPORT_CSV_AVAILABLE && <UserExportTasksButton />}
          <Tooltip title={t('dashboard:Toolbar.enableFullScreen')} delay={400}>
            <Button
              variant="flat"
              icon={<FullIcon className={styles.icon} />}
              title={t('dashboard:Toolbar.enableFullScreen')}
              onClick={handleEnterFullscreen}
              className={styles.toolbarIcon}
            />
          </Tooltip>
        </div>
      </section>
      {actualTab && (
        <Workbench
          key={actualTab.id}
          isFullscreenMode={isFullscreenMode}
          tab={actualTab}
          workspaceRef={workspaceRef}
          onExitFullscreen={exitFullscreen}
        />
      )}

      {actualTab && (
        <NewContentWidgetModal
          isOpened={!!newContentWidgetController}
          onCreateWidget={onCreateWidget}
          actualTab={actualTab}
          onCloseModal={onCloseNewContentWidgetModal}
        />
      )}

      <MasterDisconnectedModal
        isOpened={!!masterConnectionController}
        onConfirm={recoverMaster}
        onCancel={ingoreMasterRecovering}
      />
    </main>
  );
});
